import { Loader } from '@deque/cauldron-react';
import React, { useEffect, useMemo, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory, useLocation } from 'react-router-dom';

import { type v2 } from '@deque/billing-service-client';
import { type ProductSlugs } from '~/common/constants';
import { useAnalyticsInstance } from '~/common/contexts/analytics';
import { useAuthContext } from '~/common/contexts/auth';
import { useEnterprises } from '~/common/contexts/enterprises';
import {
  useFeatureFlags,
  useFeatureFlagState
} from '~/common/contexts/featureFlags';
import { useProducts } from '~/common/contexts/products';
import { useServerInfo } from '~/common/contexts/serverInfo';
import useMediaQuery from '~/common/hooks/useMediaQuery';
import useV2EnterpriseMembers from '~/common/hooks/useV2EnterpriseMembers';
import useV2ListLicenses from '~/common/hooks/useV2ListLicenses';
import billingClient from '~/common/utils/billing-client/client-v2';
import {
  AccessTypes,
  memberHasAccepted,
  type EnterpriseMembers
} from '~/common/utils/billing-client/enterprises/enterprise-members-v2'; // separate the imports from client-v2 for convenience in testing
import EditAccess from '../components/UserManagement/EditAccess/EditAccess';
import type { AvailableLicense } from '../components/UserManagement/UserManagement';
import UserManagement from '../components/UserManagement/UserManagement';
import useExportUsersToCSV from '../hooks/useExportUsersToCSV';
import { useUserManagementFilters } from '../hooks/useUserManagementFilters';
import { getHiddenProductSlugsFromSubs } from '../utils/get-hidden-product-slugs-from-subs';

export type UserAccessChanges = {
  grantGeneralProducts: v2.Product[];
  grantAdminProducts: v2.Product[];
  grantViewProducts: v2.Product[];
  removeProducts: v2.Product[];
};

interface UserManagementContainerProps {
  editingAccess?: boolean;
  // For testing purposes only
  initialSelectedMembers?: EnterpriseMembers.PendingOrAcceptedMember[];
}

const UserManagementContainer = ({
  editingAccess,
  initialSelectedMembers = []
}: UserManagementContainerProps): JSX.Element => {
  const { t } = useTranslation();
  const wide = useMediaQuery('(min-width: 49rem)');
  const narrow = useMediaQuery('(max-width: 31rem)');
  const history = useHistory();
  const location = useLocation();
  const { user } = useAuthContext();
  const {
    activeEnterprise,
    loading: loadingEnterprises,
    error: enterprisesError
  } = useEnterprises();
  const { featureFlags } = useFeatureFlags();
  const userActivityEnabled = useFeatureFlagState('user_activity_v1');

  /* there is always a user and enterprise id due to the Protected Route */
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const token = user!.token;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const enterpriseId = activeEnterprise!.id;
  const loaderRef = React.useRef<HTMLDivElement>(null);

  const {
    getProductBySlug,
    getProductById,
    loading: loadingProducts,
    products,
    error: productError
  } = useProducts();
  const {
    licenses,
    loading: loadingLicenses,
    error: licenseError,
    refresh: refreshLicenses
  } = useV2ListLicenses(token, {
    enterpriseId: enterpriseId || null
  });
  const {
    pendingOrAcceptedMembers,
    pendingUsers,
    loading: loadingMembers,
    error: memberError,
    refresh: refreshMembers
  } = useV2EnterpriseMembers({ token, enterpriseId });

  const { serverInfo, loading: loadingServerInfo } = useServerInfo();

  const { processData } = useExportUsersToCSV();

  const analytics = useAnalyticsInstance('axe-account');

  useEffect(() => {
    loaderRef.current?.focus();
  }, [loaderRef.current]);

  // Products that are expired and do not have a free tier or depends on feature flag state
  const hiddenProductSlugs = activeEnterprise
    ? getHiddenProductSlugsFromSubs(
        activeEnterprise.subscriptions,
        getProductBySlug,
        featureFlags
      )
    : [];

  const {
    isFiltered,
    filteredMembers,
    filteredPendingUsers,
    allowedFilterValuesAndLabels,
    appliedFilters,
    handleApplyFilters,
    searchQuery,
    setSearchQuery
  } = useUserManagementFilters({
    members: pendingOrAcceptedMembers,
    pendingUsers: pendingUsers,
    activeEnterprise,
    hiddenProductSlugs
  });

  const isMailerEnabled = useMemo(() => {
    return !!(!loadingServerInfo && serverInfo?.mailerEnabled);
  }, [loadingServerInfo, serverInfo]);

  const [loadingAction, setLoadingAction] = useState<boolean>(false);
  const [errMessage, setErrMessage] = useState<string | null>(null);
  const [confirmationMsg, setConfirmationMsg] = useState<string | null>(null);
  const [selectedMembers, setSelectedMembers] = useState<
    EnterpriseMembers.PendingOrAcceptedMember[]
  >(initialSelectedMembers);
  const [userAccessChanges, setUserAccessChanges] =
    useState<UserAccessChanges>();

  const variant = wide ? 'wide' : narrow ? 'narrow' : 'medium';
  const isMobile = ['narrow', 'medium'].includes(variant);
  const isLoading =
    loadingLicenses || loadingProducts || loadingEnterprises || loadingMembers;

  const hasError = [
    licenseError,
    productError,
    memberError,
    enterprisesError
  ].some(error => error instanceof Error);

  // process licenses for table
  const availableLicenses: AvailableLicense[] =
    licenses?.reduce((acc, license) => {
      const product = getProductById(license.product_id);
      if (
        product &&
        !hiddenProductSlugs.includes(product.slug as ProductSlugs)
      ) {
        acc.push({
          product_name: product.name,
          product_slug: product.slug,
          remaining_count:
            license.total !== null
              ? license.total - license.used - license.pending
              : null
        });
      }
      return acc;
    }, [] as AvailableLicense[]) || [];

  const filteredLicenses = licenses?.filter(license => {
    const product = getProductById(license.product_id);
    return (
      product && !hiddenProductSlugs.includes(product.slug as ProductSlugs)
    );
  });

  const onAddUser = (
    type: 'product' | 'admin' | 'member',
    productSlug?: string
  ): void => {
    switch (type) {
      case 'product':
        if (!productSlug) {
          throw new Error('Type "product" requires a product slug');
        }
        return history.push(`/user-access/${productSlug}/add-users`);
      case 'admin':
        return history.push('/user-access/axe-account/add-admin');
      case 'member':
        return history.push('/user-access/axe-account/add-member');
      default:
        throw new Error(`Type "${type}" is not a valid type`);
    }
  };

  const onResend = async (
    member: EnterpriseMembers.PendingMember
  ): Promise<void> => {
    setErrMessage(null);
    setConfirmationMsg(null);
    setLoadingAction(true);

    try {
      await billingClient.enterpriseInvitations.resend({
        token,
        enterpriseId,
        invitationId: member.invitation_id
      });
      setConfirmationMsg(
        t('The invitation to {{ email }} was successfully resent.', {
          email: member.email
        })
      );
    } catch {
      setErrMessage(
        t('Failed to resend the invitation to {{ email }}. Please try again.', {
          email: member.email
        })
      );
    }

    setLoadingAction(false);
  };

  const pendingSelectedMembers = selectedMembers.filter(
    member => !memberHasAccepted(member)
  ) as EnterpriseMembers.PendingMember[];

  const onBulkResendInvitations = async (): Promise<void> => {
    setErrMessage(null);
    setConfirmationMsg(null);
    setLoadingAction(true);

    let failedResends = 0;

    const batchSize = 10;
    const batches = [];
    for (let i = 0; i < pendingSelectedMembers.length; i += batchSize) {
      batches.push(pendingSelectedMembers.slice(i, i + batchSize));
    }

    for (const batch of batches) {
      const batchPromises = batch.map(async member => {
        try {
          await billingClient.enterpriseInvitations.resend({
            token,
            enterpriseId,
            invitationId: member.invitation_id
          });
          return { success: true };
        } catch {
          return { success: false };
        }
      });

      const results = await Promise.all(batchPromises);
      failedResends += results.filter(result => !result.success).length;
    }

    if (failedResends > 0) {
      setErrMessage(
        t('Failed to resend the invitations to some users. Please try again.')
      );
    } else {
      const pluralOrSingularMessage =
        pendingSelectedMembers.length === 1
          ? t('Successfully sent {{ invitationCount }} invitation.', {
              invitationCount: pendingSelectedMembers.length
            })
          : t('Successfully sent {{ invitationCount }} invitations.', {
              invitationCount: pendingSelectedMembers.length
            });
      setConfirmationMsg(pluralOrSingularMessage);
    }

    setLoadingAction(false);
  };

  const onRemoveUser = async (
    member: EnterpriseMembers.PendingOrAcceptedMember
  ): Promise<void> => {
    setErrMessage(null);
    setConfirmationMsg(null);
    setLoadingAction(true);

    try {
      if (memberHasAccepted(member)) {
        await billingClient.enterpriseMembers.remove({
          token,
          enterpriseId,
          memberId: member.user_id
        });
        refreshMembers();
        refreshLicenses();
        setSelectedMembers(
          selectedMembers.filter(m => m.email !== member.email)
        );
      } else {
        await billingClient.enterpriseInvitations.delete({
          token,
          enterpriseId,
          invitationId: member.invitation_id
        });
        refreshMembers();
        refreshLicenses();
        setSelectedMembers(
          selectedMembers.filter(m => m.email !== member.email)
        );
      }
      setConfirmationMsg(
        t('{{ email }} was successfully removed.', { email: member.email })
      );
    } catch {
      setErrMessage(
        t('Failed to remove {{ email }}. Please try again.', {
          email: member.email
        })
      );
    }

    setLoadingAction(false);
  };

  const selectedRemoveMembers = selectedMembers.filter(
    member => member.email !== user?.email
  );

  const onBulkRemoveUsers = async (): Promise<void> => {
    setErrMessage(null);
    setConfirmationMsg(null);
    setLoadingAction(true);

    let failedRemovals = 0;

    const batchSize = 10;
    const batches = [];

    for (let i = 0; i < selectedRemoveMembers.length; i += batchSize) {
      batches.push(selectedRemoveMembers.slice(i, i + batchSize));
    }

    for (const batch of batches) {
      const batchPromises = batch.map(async member => {
        try {
          if (memberHasAccepted(member)) {
            await billingClient.enterpriseMembers.remove({
              token,
              enterpriseId,
              memberId: member.user_id
            });
          } else {
            await billingClient.enterpriseInvitations.delete({
              token,
              enterpriseId,
              invitationId: member.invitation_id
            });
          }
          return { success: true };
        } catch {
          return { success: false };
        }
      });

      const results = await Promise.all(batchPromises);
      failedRemovals += results.filter(result => !result.success).length;
    }

    if (failedRemovals > 0) {
      const successfulRemovals = selectedRemoveMembers.length - failedRemovals;
      setErrMessage(
        t(
          'Failed to remove {{ failedRemovals }} users. {{ successfulRemovals }} users were removed. Please try again.',
          { failedRemovals, successfulRemovals }
        )
      );
    } else {
      const pluralOrSingularMessage =
        selectedRemoveMembers.length === 1
          ? t('{{ membersRemoved }} user was successfully removed.', {
              membersRemoved: selectedRemoveMembers.length
            })
          : t('{{ membersRemoved }} users were successfully removed.', {
              membersRemoved: selectedRemoveMembers.length
            });
      setConfirmationMsg(pluralOrSingularMessage);
    }

    refreshMembers();
    refreshLicenses();
    setSelectedMembers([]);
    setLoadingAction(false);
  };

  const selectedAcceptedMembers = selectedMembers.filter(member =>
    memberHasAccepted(member)
  ) as EnterpriseMembers.AcceptedMember[];

  const onEditEmailSuccess = (newEmail: string) => {
    setErrMessage(null);
    refreshMembers();
    setConfirmationMsg(
      t(
        'Email successfully updated to {{ newEmail }}. A verification email has been sent.',
        { newEmail }
      )
    );
  };

  const onBulkEditAccess = async (): Promise<void> => {
    setErrMessage(null);
    setConfirmationMsg(null);
    setLoadingAction(true);

    const keycloakIds = selectedAcceptedMembers.map(member => member.user_id);

    try {
      const updateGeneralPromises = (
        userAccessChanges?.grantGeneralProducts || []
      ).map(changes =>
        billingClient.enterpriseMembers.bulkUpdateProductAccess({
          token,
          enterpriseId,
          keycloakIds,
          productId: changes.id,
          accessType: AccessTypes.GENERAL
        })
      );

      const updateAdminPromises = (
        userAccessChanges?.grantAdminProducts || []
      ).map(changes =>
        billingClient.enterpriseMembers.bulkUpdateProductAccess({
          token,
          enterpriseId,
          keycloakIds,
          productId: changes.id,
          accessType: AccessTypes.ADMIN
        })
      );

      const updateViewPromises = (
        userAccessChanges?.grantViewProducts || []
      ).map(changes =>
        billingClient.enterpriseMembers.bulkUpdateProductAccess({
          token,
          enterpriseId,
          keycloakIds,
          productId: changes.id,
          accessType: AccessTypes.VIEW
        })
      );

      const removePromises = (userAccessChanges?.removeProducts || []).map(
        changes =>
          billingClient.enterpriseMembers.bulkRemoveProductAccess({
            token,
            enterpriseId,
            keycloakIds,
            productId: changes.id
          })
      );

      await Promise.all([
        ...updateGeneralPromises,
        ...updateAdminPromises,
        ...updateViewPromises,
        ...removePromises
      ]);

      const singularOrPluralMessage =
        selectedAcceptedMembers.length === 1
          ? t('Access changes saved for 1 user.')
          : t('Access changes saved for {{ membersCount }} users.', {
              membersCount: selectedAcceptedMembers.length
            });
      setConfirmationMsg(singularOrPluralMessage);
    } catch {
      setErrMessage(
        t('Failed to edit access for some users. Please try again.')
      );
    } finally {
      setLoadingAction(false);
      refreshMembers();
      refreshLicenses();
      window.setTimeout(() => {
        history.push('/user-access');
      }, 500);
    }
  };

  const onDismiss = () => {
    setConfirmationMsg(null);
  };

  const onExport = () => {
    const headings = [
      'user_id',
      'first_name',
      'last_name',
      'email',
      'product_access',
      'status',
      ...(userActivityEnabled ? ['last_activity'] : [])
    ];

    const csvContent = processData(filteredMembers, headings);
    // Create data rows

    const today = new Date();

    // Create blob and download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    // Create download link and click it
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute(
      'download',
      `users_export_${
        today.getMonth() + 1
      }-${today.getDate()}-${today.getFullYear()}.csv`
    );
    document.body.appendChild(link);
    link.click();
    analytics.userManagementExportSuccess();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (
      location.pathname === '/user-access' &&
      initialSelectedMembers.length === 0
    ) {
      setSelectedMembers([]);
      setUserAccessChanges(undefined);
    }
  }, [location.pathname, initialSelectedMembers.length]);

  if (
    location.pathname === '/user-access/edit-access' &&
    selectedAcceptedMembers.length === 0
  ) {
    return <Redirect to="/user-access" />;
  }

  if (isLoading) {
    return <Loader label={t('Loading...')} ref={loaderRef} tabIndex={-1} />;
  }

  return editingAccess ? (
    <EditAccess
      selectedMembers={selectedAcceptedMembers}
      licenses={filteredLicenses || []}
      products={products || []}
      userAccessChanges={userAccessChanges}
      setUserAccessChanges={setUserAccessChanges}
      handleBulkEditAccess={onBulkEditAccess}
      loadingAction={loadingAction}
    />
  ) : (
    <UserManagement
      isFiltered={isFiltered}
      filteredMembers={filteredMembers || []}
      filteredPendingUsers={filteredPendingUsers || []}
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      currentUserId={user!.id}
      availableLicenses={availableLicenses}
      getProductBySlug={getProductBySlug}
      onAddUser={onAddUser}
      onResend={onResend}
      onBulkResendInvitations={onBulkResendInvitations}
      onRemoveUser={onRemoveUser}
      onBulkRemoveUsers={onBulkRemoveUsers}
      onEditEmailSuccess={onEditEmailSuccess}
      enterpriseId={enterpriseId}
      token={token}
      onDismiss={onDismiss}
      //unlike general toasts, action toasts do not block the table
      loadingAction={loadingAction}
      hasError={hasError}
      errMessage={errMessage}
      confirmationMsg={confirmationMsg}
      isMobile={isMobile}
      hiddenProductSlugs={hiddenProductSlugs}
      isMailerEnabled={isMailerEnabled}
      handleApplyFilters={handleApplyFilters}
      appliedFilters={appliedFilters}
      allowedFilterValuesAndLabels={allowedFilterValuesAndLabels}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      onExport={onExport}
      selectedMembers={selectedMembers}
      setSelectedMembers={setSelectedMembers}
      pendingSelectedMembers={pendingSelectedMembers}
      removeSelectedMembers={selectedRemoveMembers}
      selectedAcceptedMembers={selectedAcceptedMembers}
    />
  );
};

export default UserManagementContainer;
